/**
 * LayerValuesMultiselectPicker · 维度链 v6 · 自由选择 picker (2026-05-18 截图 9/10/11)
 *
 * 触发: 后端 ask.picker.kind === 'layer_values_multiselect' 时, ChoiceOptionsCard 路由到本组件.
 *
 * 流程:
 *  1. mount → fetchLayerValues(target_layer_id) 拉当前 layer 全部 values (默认 200, 上限 500)
 *  2. 渲染搜索框 + checkbox 组 (默认全选) + 计数
 *  3. 用户勾选完点「确认保留」→ postLayerValuesSelect(keys[]) 写回 (服务端套 keep_keys filter)
 *  4. 成功 → getSession 拉最新 chain (含 chain_version / expanded_size) → dispatch
 *     setClassifyPreview → FolderPlanPreview useEffect 自动重拉预览
 *  5. 组件置为 done 态, 展示「✅ 已保留 N 个」
 *
 * 设计:
 *  · LLM 永不接触具体 values, 服务端从 ai_classify_dimension_layer.values_json 取
 *    → 套 filter → 写回. 零幻觉 + 用户体验直观.
 *  · max_choice 前端强约束 (后端 silently skip 编造 keys, 但 UI 更早阻断给用户反馈).
 */

import React, { useEffect, useMemo, useState } from 'react'
import { Checkbox, Button, Empty, Spin, message, Input } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../../../../../app/hooks'
import {
  fetchLayerValues,
  postLayerValuesSelect,
  getSession
} from '../../../../../services/sessionApi'
import { setClassifyPreview } from '../../../../../../../slices/aiClassify'
import type {
  LayerValuesPickerSchema,
  LayerValuesResponse,
  LayerSummary
} from '../../../../../../../types/ai-classify'
import styles from './card.module.scss'

interface Props {
  picker: LayerValuesPickerSchema
  sessionId: string
  /** 卡片头部问句 (来自 ask.question, 复用 ChoiceOptionsCard 的样式) */
  question: string
  /** 历史回放时 true, 禁用所有交互 */
  disabled?: boolean
}

const LayerValuesMultiselectPicker: React.FC<Props> = ({
  picker,
  sessionId,
  question,
  disabled
}) => {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((s: any) => s.group.groupId)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [resp, setResp] = useState<LayerValuesResponse | null>(null)
  const [keyword, setKeyword] = useState('')
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)
  const [keptCount, setKeptCount] = useState(0)

  // 拉数据 + 默认全选
  useEffect(() => {
    if (!sessionId || !groupID || !picker.target_layer_id) return
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchLayerValues(groupID, sessionId, picker.target_layer_id, {
      keyword,
      limit: 500
    })
      .then((r) => {
        if (cancelled) return
        setResp(r)
        if (!keyword) {
          // 初次加载默认全选; keyword 搜索时不动 selection (跨 keyword 保留用户已勾)
          setSelectedKeys(r.values.map((v) => v.key))
        }
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message || '加载候选值失败')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [sessionId, groupID, picker.target_layer_id, keyword])

  const maxChoice = picker.max_choice || 0
  const overLimit = maxChoice > 0 && selectedKeys.length > maxChoice
  const canSubmit =
    selectedKeys.length > 0 && !overLimit && !submitting && !disabled

  const handleConfirm = async (): Promise<void> => {
    if (!canSubmit) return
    setSubmitting(true)
    try {
      const r = await postLayerValuesSelect(
        groupID,
        sessionId,
        picker.target_layer_id,
        selectedKeys
      )
      // 拉最新 session detail 同步 redux chain 状态, 让 FolderPlanPreview 重拉
      try {
        const detail = await getSession(groupID, sessionId)
        dispatch(
          setClassifyPreview({
            sessionId: detail.session_id,
            chainVersion: detail.chain_version,
            expandedSize: detail.expanded_size,
            layerCount: detail.layer_count,
            layersSummary: (detail.layers_summary || []) as LayerSummary[]
          })
        )
      } catch {
        /* getSession 失败不阻断 picker 成功流程, 下一轮 SSE/preview ETag miss 会自然刷 */
      }
      setKeptCount(r.kept_count)
      setDone(true)
      message.success(`已保留 ${r.kept_count} 个`)
    } catch (e) {
      message.error((e as Error).message || '保存失败, 请重试')
    } finally {
      setSubmitting(false)
    }
  }

  // ── 已完成态 ──
  if (done) {
    return (
      <div className={styles.card}>
        <div className={styles.question}>{question}</div>
        <div
          className={styles.optionItemPicked}
          style={{ padding: '12px 16px' }}
        >
          ✅ 已保留 {keptCount} 个, 分类方案已更新
        </div>
      </div>
    )
  }

  // ── 历史回放态 (disabled) ──
  if (disabled) {
    return (
      <div className={styles.card}>
        <div className={styles.question}>{question}</div>
        <div style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13 }}>
          (历史会话, 已不可操作)
        </div>
      </div>
    )
  }

  // ── 加载/错误态 ──
  if (loading) {
    return (
      <div className={styles.card}>
        <div className={styles.question}>{question}</div>
        <div style={{ padding: '24px 16px', textAlign: 'center' }}>
          <Spin size="small" />{' '}
          <span style={{ marginLeft: 8 }}>加载候选值中...</span>
        </div>
      </div>
    )
  }
  if (error) {
    return (
      <div className={styles.card}>
        <div className={styles.question}>{question}</div>
        <div style={{ padding: '12px 16px', color: '#ef4444' }}>{error}</div>
      </div>
    )
  }
  if (!resp || resp.values.length === 0) {
    return (
      <div className={styles.card}>
        <div className={styles.question}>{question}</div>
        <Empty
          description={
            keyword ? `没有匹配「${keyword}」的项` : '当前层没有候选值'
          }
          style={{ padding: '12px 0' }}
        />
        {keyword && (
          <Button
            size="small"
            onClick={() => setKeyword('')}
            style={{ marginTop: 8 }}
          >
            清空搜索
          </Button>
        )}
      </div>
    )
  }

  // ── 选择态 ──
  return (
    <div className={styles.card}>
      <div className={styles.question}>{question}</div>

      <Input.Search
        placeholder="搜索（按名称过滤）"
        size="small"
        allowClear
        onSearch={(v) => setKeyword(v.trim())}
        style={{ marginBottom: 12 }}
      />

      <div
        style={{
          maxHeight: 320,
          overflowY: 'auto',
          border: '1px solid #e2e8f0',
          borderRadius: 6,
          padding: '8px 12px'
        }}
      >
        <Checkbox.Group
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            width: '100%'
          }}
          value={selectedKeys}
          onChange={(vals) => setSelectedKeys(vals.map(String))}
        >
          {resp.values.map((v) => (
            <Checkbox key={v.key} value={v.key}>
              {v.name}
              {v.parent_key && (
                <span style={{ marginLeft: 8, color: '#94a3b8', fontSize: 12 }}>
                  (父: {v.parent_key})
                </span>
              )}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </div>

      <FooterBar
        selected={selectedKeys.length}
        total={resp.matched}
        maxChoice={maxChoice}
        overLimit={overLimit}
        submitting={submitting}
        canSubmit={canSubmit}
        onSelectAll={() => setSelectedKeys(resp.values.map((v) => v.key))}
        onClear={() => setSelectedKeys([])}
        onConfirm={handleConfirm}
      />
    </div>
  )
}

interface FooterProps {
  selected: number
  total: number
  maxChoice: number
  overLimit: boolean
  submitting: boolean
  canSubmit: boolean
  onSelectAll: () => void
  onClear: () => void
  onConfirm: () => void
}

const FooterBar: React.FC<FooterProps> = ({
  selected,
  total,
  maxChoice,
  overLimit,
  submitting,
  canSubmit,
  onSelectAll,
  onClear,
  onConfirm
}) => {
  const tip = useMemo(() => {
    if (overLimit) return `最多 ${maxChoice} 个，请减少选择`
    if (maxChoice > 0) return `已选 ${selected} / ${total}（上限 ${maxChoice}）`
    return `已选 ${selected} / ${total}`
  }, [selected, total, maxChoice, overLimit])

  return (
    <div
      style={{
        marginTop: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        flexWrap: 'wrap'
      }}
    >
      <span style={{ fontSize: 13, color: overLimit ? '#ef4444' : '#475569' }}>
        {tip}
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button size="small" onClick={onSelectAll}>
          全选
        </Button>
        <Button size="small" onClick={onClear}>
          清空
        </Button>
        <Button
          size="small"
          type="primary"
          loading={submitting}
          disabled={!canSubmit}
          onClick={onConfirm}
        >
          确认保留
        </Button>
      </div>
    </div>
  )
}

export default LayerValuesMultiselectPicker
